import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { StarRating } from "./StarRating";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { ThumbsUp, ThumbsDown, BookOpen, UserIcon, PenLine, CheckCircle } from "lucide-react";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  certificateId: string;
  registrationId: string;
  onFeedbackSubmitted: () => void;
}

export function FeedbackDialog({
  open,
  onOpenChange,
  certificateId,
  registrationId,
  onFeedbackSubmitted,
}: FeedbackDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<string>("step1");
  const [progress, setProgress] = useState<number>(0);
  
  // Section 1: Teaching Grades
  const [teachingGrades, setTeachingGrades] = useState<string[]>([]);
  const [otherGrade, setOtherGrade] = useState<string>("");
  
  // Section 2: Participation
  const [participated, setParticipated] = useState<string | null>(null);
  
  // Section 3: Knowledge and Ratings
  const [priorKnowledge, setPriorKnowledge] = useState<string | null>(null);
  const [afterKnowledge, setAfterKnowledge] = useState<string | null>(null);
  const [expectationRating, setExpectationRating] = useState<number | null>(null);
  const [learningStyleRating, setLearningStyleRating] = useState<number | null>(null);
  const [materialsRating, setMaterialsRating] = useState<number | null>(null);
  const [overallRating, setOverallRating] = useState<number | null>(null);
  const [recommendRating, setRecommendRating] = useState<number | null>(null);
  
  // Section 4: Descriptive Feedback
  const [sessionWords, setSessionWords] = useState<string>("");
  const [facilitatorWords, setFacilitatorWords] = useState<string>("");
  const [additionalFeedback, setAdditionalFeedback] = useState<string>("");

  console.log('[FEEDBACK-DEBUG] Dialog opened with certificateId:', certificateId);
  console.log('[FEEDBACK-DEBUG] Dialog opened with registrationId:', registrationId);

  const handleGradeSelection = (grade: string) => {
    setTeachingGrades(
      teachingGrades.includes(grade)
        ? teachingGrades.filter(g => g !== grade)
        : [...teachingGrades, grade]
    );
  };

  const nextStep = () => {
    if (activeStep === "step1") {
      setActiveStep("step2");
      setProgress(25);
    } else if (activeStep === "step2") {
      if (!participated) {
        toast({
          title: "Participation Required",
          description: "Please indicate whether you participated in the session.",
          variant: "destructive",
        });
        return;
      }
      
      if (participated === "No") {
        setActiveStep("summary");
        setProgress(100);
      } else {
        setActiveStep("step3");
        setProgress(50);
      }
    } else if (activeStep === "step3") {
      setActiveStep("step4");
      setProgress(75);
    } else if (activeStep === "step4") {
      setActiveStep("summary");
      setProgress(100);
    }
  };

  const prevStep = () => {
    if (activeStep === "step2") {
      setActiveStep("step1");
      setProgress(0);
    } else if (activeStep === "step3") {
      setActiveStep("step2");
      setProgress(25);
    } else if (activeStep === "step4") {
      setActiveStep("step3");
      setProgress(50);
    } else if (activeStep === "summary") {
      if (participated === "No") {
        setActiveStep("step2");
        setProgress(25);
      } else {
        setActiveStep("step4");
        setProgress(75);
      }
    }
  };

  const handleSubmit = async () => {
    if (!participated) {
      toast({
        title: "Participation Required",
        description: "Please indicate whether you participated in the session.",
        variant: "destructive",
      });
      return;
    }

    if (participated === "Yes" && overallRating === null) {
      toast({
        title: "Rating Required",
        description: "Please provide an overall rating for the session.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const feedbackData = {
        teaching_grades: teachingGrades,
        other_grade: otherGrade,
        
        ...(participated === "Yes" && {
          prior_knowledge: priorKnowledge,
          after_knowledge: afterKnowledge,
          expectation_rating: expectationRating,
          learning_style_rating: learningStyleRating,
          materials_rating: materialsRating,
          overall_rating: overallRating,
          recommend_rating: recommendRating,
          session_words: sessionWords,
          facilitator_words: facilitatorWords,
          feedback: additionalFeedback,
        }),
        ...(participated === "No" && {
          feedback: "Did not participate"
        })
      };
      
      console.log('[FEEDBACK-DEBUG] Submitting feedback data:', feedbackData);
      
      const { error: registrationError } = await supabase
        .from("course_registrations")
        .update(feedbackData)
        .eq("id", registrationId);

      if (registrationError) {
        console.error('[FEEDBACK-ERROR] Registration update failed:', registrationError);
        throw registrationError;
      }
      
      const { error: certificateError } = await supabase
        .from("certificates")
        .update({ feedback_submitted: true })
        .eq("id", certificateId);
      
      if (certificateError) {
        console.error('[FEEDBACK-ERROR] Certificate update failed:', certificateError);
        throw certificateError;
      }
      
      const { data: verifyData, error: verifyError } = await supabase
        .from('certificates')
        .select('feedback_submitted')
        .eq('id', certificateId)
        .single();
        
      if (verifyError) {
        console.error('[FEEDBACK-ERROR] Verification check failed:', verifyError);
      } else if (!verifyData?.feedback_submitted) {
        console.error('[FEEDBACK-ERROR] Feedback status not updated correctly. Value is:', verifyData?.feedback_submitted);
        await supabase
          .from("certificates")
          .update({ feedback_submitted: true })
          .eq("id", certificateId);
      }

      toast({
        title: "Thank you!",
        description: "Your feedback has been submitted successfully.",
      });
      
      onOpenChange(false);
      setTimeout(() => {
        onFeedbackSubmitted();
      }, 500);
    } catch (error) {
      console.error("[FEEDBACK-ERROR] Error submitting feedback:", error);
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const getKnowledgeLabel = (level: string) => {
    switch(level) {
      case "deep": return "Deep / Strong";
      case "surface": return "Surface / Developing";
      case "limited": return "Limited / Emerging";
      case "zero": return "Zero / Nonexistent";
      default: return "";
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case "step1":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium font-section text-primary">About You</h3>
            
            <div className="space-y-3">
              <Label className="text-base font-bold font-body">
                Which grades do you teach/support?
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {["PreK", "K-2", "3-5", "6-8", "9-12", "adults"].map((grade) => (
                  <div key={grade} className="flex items-center space-x-2">
                    <Checkbox 
                      id={`grade-${grade}`} 
                      checked={teachingGrades.includes(grade)}
                      onCheckedChange={() => handleGradeSelection(grade)}
                    />
                    <Label htmlFor={`grade-${grade}`} className="font-body">{grade}</Label>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="grade-other" 
                  checked={teachingGrades.includes("Other")}
                  onCheckedChange={() => handleGradeSelection("Other")}
                />
                <Label htmlFor="grade-other" className="font-body">Other</Label>
              </div>
              
              {teachingGrades.includes("Other") && (
                <div className="pl-6">
                  <Input
                    placeholder="Please specify"
                    value={otherGrade}
                    onChange={(e) => setOtherGrade(e.target.value)}
                    className="max-w-sm font-body"
                  />
                </div>
              )}
            </div>
          </div>
        );
      
      case "step2":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium font-section text-primary">Session Participation</h3>
            
            <div className="space-y-3">
              <Label className="text-base font-bold font-body">
                Did you participate in the session? <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <Button
                  type="button"
                  variant={participated === "Yes" ? "default" : "outline"}
                  className={`p-4 h-auto flex flex-col items-center gap-2 ${participated === "Yes" ? "ring-2 ring-primary" : ""}`}
                  onClick={() => setParticipated("Yes")}
                >
                  <ThumbsUp className="h-8 w-8 text-green-500" />
                  <span className="font-body text-base">Yes, I participated</span>
                </Button>
                
                <Button
                  type="button"
                  variant={participated === "No" ? "default" : "outline"}
                  className={`p-4 h-auto flex flex-col items-center gap-2 ${participated === "No" ? "ring-2 ring-primary" : ""}`}
                  onClick={() => setParticipated("No")}
                >
                  <ThumbsDown className="h-8 w-8 text-red-500" />
                  <span className="font-body text-base">No, I did not participate</span>
                </Button>
              </div>
            </div>
          </div>
        );
      
      case "step3":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium font-section text-primary">Knowledge & Learning</h3>
            
            <div className="space-y-3">
              <Label className="text-base font-bold font-body">
                Rate your knowledge/skills as they relate(d) to the content of this session. <span className="text-red-500">*</span>
              </Label>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left font-body"></th>
                      <th className="text-center px-2 py-1 font-body">deep / strong</th>
                      <th className="text-center px-2 py-1 font-body">surface / developing</th>
                      <th className="text-center px-2 py-1 font-body">limited / emerging</th>
                      <th className="text-center px-2 py-1 font-body">zero / nonexistent</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 font-body"><strong>Prior to the session</strong></td>
                      <td className="text-center">
                        <RadioGroup value={priorKnowledge || ""} onValueChange={setPriorKnowledge} className="flex justify-center">
                          <RadioGroupItem value="deep" id="prior-deep" className="mx-auto" />
                        </RadioGroup>
                      </td>
                      <td className="text-center">
                        <RadioGroup value={priorKnowledge || ""} onValueChange={setPriorKnowledge} className="flex justify-center">
                          <RadioGroupItem value="surface" id="prior-surface" className="mx-auto" />
                        </RadioGroup>
                      </td>
                      <td className="text-center">
                        <RadioGroup value={priorKnowledge || ""} onValueChange={setPriorKnowledge} className="flex justify-center">
                          <RadioGroupItem value="limited" id="prior-limited" className="mx-auto" />
                        </RadioGroup>
                      </td>
                      <td className="text-center">
                        <RadioGroup value={priorKnowledge || ""} onValueChange={setPriorKnowledge} className="flex justify-center">
                          <RadioGroupItem value="zero" id="prior-zero" className="mx-auto" />
                        </RadioGroup>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 font-body"><strong>After participating in the session</strong></td>
                      <td className="text-center">
                        <RadioGroup value={afterKnowledge || ""} onValueChange={setAfterKnowledge} className="flex justify-center">
                          <RadioGroupItem value="deep" id="after-deep" className="mx-auto" />
                        </RadioGroup>
                      </td>
                      <td className="text-center">
                        <RadioGroup value={afterKnowledge || ""} onValueChange={setAfterKnowledge} className="flex justify-center">
                          <RadioGroupItem value="surface" id="after-surface" className="mx-auto" />
                        </RadioGroup>
                      </td>
                      <td className="text-center">
                        <RadioGroup value={afterKnowledge || ""} onValueChange={setAfterKnowledge} className="flex justify-center">
                          <RadioGroupItem value="limited" id="after-limited" className="mx-auto" />
                        </RadioGroup>
                      </td>
                      <td className="text-center">
                        <RadioGroup value={afterKnowledge || ""} onValueChange={setAfterKnowledge} className="flex justify-center">
                          <RadioGroupItem value="zero" id="after-zero" className="mx-auto" />
                        </RadioGroup>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-base font-bold font-body">
                To what degree did this session meet your expectations? <span className="text-red-500">*</span>
              </Label>
              <div className="flex flex-col sm:flex-row justify-between items-center gap-2 bg-accent-subtle/10 p-4 rounded-md">
                <span className="text-sm font-body">Did not meet</span>
                <div className="flex gap-4">
                  {[1, 2, 3, 4].map((rating) => (
                    <Button
                      key={rating}
                      type="button"
                      variant={expectationRating === rating ? "default" : "outline"}
                      className={`h-10 w-10 rounded-full p-0 ${expectationRating === rating ? 'bg-primary text-white' : ''}`}
                      onClick={() => setExpectationRating(rating)}
                    >
                      {rating}
                    </Button>
                  ))}
                </div>
                <span className="text-sm font-body">Exceeded</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-base font-bold font-body">
                Did you feel that the workshop satisfied your preferred learning styles? <span className="text-red-500">*</span>
              </Label>
              <div className="flex flex-col sm:flex-row justify-between items-center gap-2 bg-accent-subtle/10 p-4 rounded-md">
                <span className="text-sm font-body">Did not meet</span>
                <div className="flex gap-4">
                  {[1, 2, 3, 4].map((rating) => (
                    <Button
                      key={rating}
                      type="button"
                      variant={learningStyleRating === rating ? "default" : "outline"}
                      className={`h-10 w-10 rounded-full p-0 ${learningStyleRating === rating ? 'bg-primary text-white' : ''}`}
                      onClick={() => setLearningStyleRating(rating)}
                    >
                      {rating}
                    </Button>
                  ))}
                </div>
                <span className="text-sm font-body">Exceeded</span>
              </div>
            </div>
          </div>
        );
      
      case "step4":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium font-section text-primary">Course Evaluation</h3>
            
            <div className="space-y-2">
              <Label className="text-base font-bold font-body">
                How useful did you find the worksheets and handouts? <span className="text-red-500">*</span>
              </Label>
              <div className="flex flex-col sm:flex-row justify-between items-center gap-2 bg-accent-subtle/10 p-4 rounded-md">
                <span className="text-sm font-body">Not very useful</span>
                <div className="flex gap-4">
                  {[1, 2, 3, 4].map((rating) => (
                    <Button
                      key={rating}
                      type="button"
                      variant={materialsRating === rating ? "default" : "outline"}
                      className={`h-10 w-10 rounded-full p-0 ${materialsRating === rating ? 'bg-primary text-white' : ''}`}
                      onClick={() => setMaterialsRating(rating)}
                    >
                      {rating}
                    </Button>
                  ))}
                </div>
                <span className="text-sm font-body">Extremely useful</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-base font-bold font-body">
                Overall, how would you rate this session? <span className="text-red-500">*</span>
              </Label>
              <div className="flex flex-col sm:flex-row justify-between items-center gap-2 bg-accent-subtle/10 p-4 rounded-md">
                <span className="text-sm font-body">Poor</span>
                <div className="flex gap-4">
                  {[1, 2, 3, 4].map((rating) => (
                    <Button
                      key={rating}
                      type="button"
                      variant={overallRating === rating ? "default" : "outline"}
                      className={`h-10 w-10 rounded-full p-0 ${overallRating === rating ? 'bg-primary text-white' : ''}`}
                      onClick={() => setOverallRating(rating)}
                    >
                      {rating}
                    </Button>
                  ))}
                </div>
                <span className="text-sm font-body">Excellent</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-base font-bold font-body">
                How likely is it that you would recommend the session to a friend or colleague? <span className="text-red-500">*</span>
              </Label>
              <div className="flex flex-col sm:flex-row justify-between items-center gap-2 bg-accent-subtle/10 p-4 rounded-md">
                <span className="text-sm font-body">Not at all likely</span>
                <div className="flex gap-4">
                  {[1, 2, 3, 4].map((rating) => (
                    <Button
                      key={rating}
                      type="button"
                      variant={recommendRating === rating ? "default" : "outline"}
                      className={`h-10 w-10 rounded-full p-0 ${recommendRating === rating ? 'bg-primary text-white' : ''}`}
                      onClick={() => setRecommendRating(rating)}
                    >
                      {rating}
                    </Button>
                  ))}
                </div>
                <span className="text-sm font-body">Extremely likely</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="session-words" className="text-base font-bold font-body">
                What 3 words best describe this session's content & structure?
              </Label>
              <Input
                id="session-words"
                placeholder="e.g., engaging, informative, practical"
                value={sessionWords}
                onChange={(e) => setSessionWords(e.target.value)}
                className="w-full font-body"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="facilitator-words" className="text-base font-bold font-body">
                What 3 words best describe this session's facilitator?
              </Label>
              <Input
                id="facilitator-words"
                placeholder="e.g., knowledgeable, passionate, clear"
                value={facilitatorWords}
                onChange={(e) => setFacilitatorWords(e.target.value)}
                className="w-full font-body"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="additional-feedback" className="text-base font-bold font-body">
                If you have other feedback for this particular session/facilitator, please note it here:
              </Label>
              <Textarea
                id="additional-feedback"
                placeholder="Please provide any additional feedback..."
                value={additionalFeedback}
                onChange={(e) => setAdditionalFeedback(e.target.value)}
                rows={4}
                className="w-full font-body"
              />
            </div>
          </div>
        );
        
      case "summary":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium font-section text-primary">Feedback Summary</h3>
            
            <div className="bg-accent-subtle/20 rounded-lg p-4 space-y-4">
              <div>
                <h4 className="font-bold font-section text-primary mb-1">Teaching/Support Grade Levels</h4>
                {teachingGrades.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {teachingGrades.map(grade => (
                      <span key={grade} className="bg-accent-subtle/50 px-2 py-1 rounded-md text-sm font-body">
                        {grade === "Other" && otherGrade ? `${grade}: ${otherGrade}` : grade}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic font-body">None selected</p>
                )}
              </div>
              
              <div>
                <h4 className="font-bold font-section text-primary mb-1">Participation Status</h4>
                <div className="flex items-center gap-2">
                  {participated === "Yes" ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="font-body">Participated in session</span>
                    </>
                  ) : participated === "No" ? (
                    <>
                      <ThumbsDown className="h-5 w-5 text-red-500" />
                      <span className="font-body">Did not participate</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground italic font-body">Not specified</span>
                  )}
                </div>
              </div>
              
              {participated === "Yes" && (
                <>
                  <div>
                    <h4 className="font-bold font-section text-primary mb-1">Knowledge Assessment</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-primary" />
                        <span className="font-body">Prior: {priorKnowledge ? getKnowledgeLabel(priorKnowledge) : "Not specified"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-accent" />
                        <span className="font-body">After: {afterKnowledge ? getKnowledgeLabel(afterKnowledge) : "Not specified"}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-bold font-section text-primary mb-1">Ratings</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      <div className="bg-accent-subtle/30 p-2 rounded-md">
                        <span className="font-body">Met Expectations: {expectationRating || "N/A"}/4</span>
                      </div>
                      <div className="bg-accent-subtle/30 p-2 rounded-md">
                        <span className="font-body">Learning Style: {learningStyleRating || "N/A"}/4</span>
                      </div>
                      <div className="bg-accent-subtle/30 p-2 rounded-md">
                        <span className="font-body">Materials: {materialsRating || "N/A"}/4</span>
                      </div>
                      <div className="bg-accent-subtle/30 p-2 rounded-md">
                        <span className="font-body">Overall: {overallRating || "N/A"}/4</span>
                      </div>
                      <div className="bg-accent-subtle/30 p-2 rounded-md">
                        <span className="font-body">Would Recommend: {recommendRating || "N/A"}/4</span>
                      </div>
                    </div>
                  </div>
                  
                  {(sessionWords || facilitatorWords || additionalFeedback) && (
                    <div>
                      <h4 className="font-bold font-section text-primary mb-1">Comments</h4>
                      {sessionWords && (
                        <div className="mb-2">
                          <span className="font-body text-sm text-muted-foreground">Session words:</span>
                          <p className="font-body">{sessionWords}</p>
                        </div>
                      )}
                      {facilitatorWords && (
                        <div className="mb-2">
                          <span className="font-body text-sm text-muted-foreground">Facilitator words:</span>
                          <p className="font-body">{facilitatorWords}</p>
                        </div>
                      )}
                      {additionalFeedback && (
                        <div>
                          <span className="font-body text-sm text-muted-foreground">Additional feedback:</span>
                          <p className="font-body">{additionalFeedback}</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading text-primary text-xl">Course Feedback</AlertDialogTitle>
          <AlertDialogDescription className="font-body">
            Please provide your feedback about this course. Your input helps us improve our offerings.
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="mt-2 mb-4">
          <Progress value={progress} className="h-2 w-full" />
          <div className="flex justify-between mt-1 text-xs text-muted-foreground font-caption">
            <span>Start</span>
            <span>In Progress</span>
            <span>Complete</span>
          </div>
        </div>
        
        <div className="py-4">
          {renderStepContent()}
        </div>
        
        <AlertDialogFooter className="flex justify-between sm:justify-between">
          {activeStep !== "step1" && (
            <Button 
              type="button" 
              variant="outline" 
              onClick={prevStep}
              disabled={isSubmitting}
              className="font-body"
            >
              Back
            </Button>
          )}
          <div className="flex space-x-2">
            <AlertDialogCancel disabled={isSubmitting} className="font-body">Cancel</AlertDialogCancel>
            {activeStep !== "summary" ? (
              <Button 
                onClick={nextStep} 
                disabled={isSubmitting || (activeStep === "step2" && !participated)}
                className="font-body"
              >
                Next
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={isSubmitting || !participated || (participated === "Yes" && !overallRating)}
                className="font-body"
              >
                {isSubmitting ? "Submitting..." : "Submit Feedback"}
              </Button>
            )}
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
